import { useCopilotAction } from '@copilotkit/react-core';

import { apiSlice, TagTypes } from '@/redux/slices/apiSlice';
import { useAppDispatch } from '@/redux/store';
import { TOOL_NAMES } from '@/helpers/aiChat/tools';
import type {
  GettingStartedProgress,
  GettingStartedStepKey,
} from '@/types/aiChat';

type StepCompletion = {
  tags?: { type: TagTypes; id?: string }[];
  progress?: Partial<GettingStartedProgress>;
};

// Maps each GS step to how it signals completion in the UI.
// Data-backed steps (employee count, app count, etc.) invalidate the relevant RTK cache
// tag so the query re-fetches and the checklist updates.
// Tag ids must match what each query actually provides — see organizationApiSlice:
//   - Employee/Application: providesTags uses the type only (no id)
//   - Group: providesListAndAllById → includes 'LIST'
// Storage-backed steps write a localStorage flag directly (same pattern as the UI buttons).
// Navigate-only steps (connect_hr_system, add_payment_method, connect_abm) have no backend
// tool result to hook into — completion is driven by admin confirmation via the mark tool's
// handler returning a success message, which is enough to satisfy the GS flow.
const STEP_COMPLETION: Partial<Record<GettingStartedStepKey, StepCompletion>> =
  {
    connect_hr_system: { tags: [{ type: TagTypes.Organization }] },
    add_employees: { tags: [{ type: TagTypes.Employee }] },
    invite_admins: { tags: [{ type: TagTypes.Employee }] },
    add_applications: { tags: [{ type: TagTypes.Application }] },
    connect_app_integration: { tags: [{ type: TagTypes.Application }] },
    add_groups: { tags: [{ type: TagTypes.Group, id: 'LIST' }] },
    // Invalidate MDMInformation so the cert status re-fetches and mdmEnabled updates.
    // Do NOT write skipMacMdm — that is the "skip Apple MDM" flag and would incorrectly
    // auto-complete connect_abm and configure_ztp for orgs that enrolled MDM properly.
    enable_mdm: { tags: [{ type: TagTypes.MDMInformation }] },
    add_payment_method: {
      tags: [
        { type: TagTypes.BillingInfo },
        { type: TagTypes.OrganizationPaymentMethods },
      ],
    },
    configure_employee_support: { progress: { skipItChampions: true } },
    connect_abm: { tags: [{ type: TagTypes.AdditionalInfo }] },
    configure_ztp: { progress: { ztpConfigured: true } },
    // sendAutomaticWelcomeEmails is set by an async Temporal workflow so a cache
    // invalidation would race ahead and still see false. The UI card does the same
    // thing: set inviteEmployees in localStorage immediately on API success.
    invite_employees: { progress: { inviteEmployees: true } },
    notify_team: { progress: { notifyTeam: true } },
  };

type UseMarkGettingStartedStepCompleteToolProps = {
  updateProgress: (updates: Partial<GettingStartedProgress>) => void;
};

export const useMarkGettingStartedStepCompleteTool = ({
  updateProgress,
}: UseMarkGettingStartedStepCompleteToolProps) => {
  const dispatch = useAppDispatch();

  useCopilotAction({
    name: TOOL_NAMES.MARK_GS_STEP_COMPLETE,
    description:
      'Mark a Getting Started setup step as complete in the UI checklist. ' +
      'Call this immediately after successfully completing or confirming a step, ' +
      'before moving on to the next one.',
    parameters: [
      {
        name: 'step',
        type: 'string',
        description:
          'The Getting Started step key to mark complete (e.g. "add_employees", ' +
          '"configure_employee_support", "invite_employees").',
        required: true,
      },
      {
        name: 'skipMacMdm',
        type: 'boolean',
        description:
          'When true, also marks the Mac-specific MDM steps (connect_abm, configure_ztp) ' +
          'as N/A. Use only when the org is Windows-only and Apple MDM steps do not apply.',
        required: false,
      },
    ],
    handler: async ({
      step,
      skipMacMdm: shouldSkipMacMdm,
    }: {
      step: string;
      skipMacMdm?: boolean;
    }) => {
      const action = (
        STEP_COMPLETION as Record<string, StepCompletion | undefined>
      )[step];
      if (!action) {
        return `Step "${step}" is not tracked in the UI checklist.`;
      }

      if (action.tags) {
        dispatch(apiSlice.util.invalidateTags(action.tags));
      }
      const progressUpdates: Partial<GettingStartedProgress> = {
        ...action.progress,
        ...(shouldSkipMacMdm ? { skipMacMdm: true } : {}),
      };
      if (Object.keys(progressUpdates).length > 0) {
        updateProgress(progressUpdates);
      }

      return `Step "${step}" marked as complete.`;
    },
  });
};
